import {
  isAgentParticipantId,
  parseAgentConfigurationId,
} from "@app/lib/api/people/group_key";
import type { ChatSummaryType } from "@app/types/api/chats";
import { agentAvatarVisual } from "@app/types/assistant/avatar";
import { createAvatar } from "@dicebear/core";
import * as glass from "@dicebear/glass";
import * as icons from "@dicebear/icons";
import { Avatar, cn } from "@dust-tt/sparkle";
import { useMemo } from "react";

export type ChatParticipantType = ChatSummaryType["participants"][number];

export function isAgentParticipant(participant: ChatParticipantType): boolean {
  return participant.kind === "agent" || isAgentParticipantId(participant.sId);
}

export function AvatarCluster({
  avatars,
}: {
  avatars: { key: string; name: string; visual?: string | null }[];
}) {
  const [first, second] = avatars;

  if (!first) {
    return null;
  }
  if (!second) {
    return (
      <Avatar
        size="sm"
        visual={first.visual}
        name={first.name}
        className="shrink-0 rounded-xl ring-2 ring-app-background"
      />
    );
  }
  const clustered = avatars.slice(0, 3);
  const positions =
    clustered.length === 3
      ? ["left-2 top-0", "bottom-0 left-0", "bottom-0 right-0"]
      : ["left-0 top-0", "bottom-0 right-0"];
  return (
    <div className="relative size-9 shrink-0">
      {clustered.map((avatar, index) => (
        <Avatar
          key={avatar.key}
          size="xs"
          visual={avatar.visual}
          name={avatar.name}
          className={cn(
            "absolute rounded-full ring-2 ring-app-background",
            clustered.length === 3 ? "h-5 w-5" : "h-6 w-6",
            positions[index]
          )}
        />
      ))}
    </div>
  );
}

export function ChatAvatars({
  participants,
}: {
  participants: ChatParticipantType[];
}) {
  return (
    <AvatarCluster
      avatars={participants.map((participant) => ({
        key: participant.sId,
        name: participant.fullName,
        visual: isAgentParticipant(participant)
          ? agentAvatarVisual({
              sId:
                parseAgentConfigurationId(participant.sId) ?? participant.sId,
              name: participant.fullName,
              pictureUrl: participant.image,
            })
          : participant.image,
      }))}
    />
  );
}

// The agent tiles' recipe without a face: dark line art on a soft glassy
// tile. Each thread draws its own ground and object from its id.
const workThreadPalette = [
  "e8a08c",
  "9fc4ec",
  "92d2b2",
  "eea9c9",
  "efc672",
  "b8a6ec",
  "86c9de",
];
const workThreadObjects: NonNullable<icons.Options["icon"]> = [
  "briefcase",
  "compass",
  "lightbulb",
  "map",
  "megaphone",
  "flag",
  "puzzle",
  "trophy",
  "pen",
  "palette",
  "globe",
  "envelope",
  "calculator",
  "easel",
  "gem",
  "key",
  "signpost",
  "search",
  "send",
  "magic",
  "lightningCharge",
  "book",
  "boxSeam",
  "stopwatch",
];
const workThreadInk = "#1f2328";

function svgDataUri(svg: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function workThreadArt(seed: string): string {
  const ground = createAvatar(glass, {
    seed,
    backgroundColor: workThreadPalette,
  }).toString();
  const object = createAvatar(icons, {
    seed,
    icon: workThreadObjects,
    scale: 70,
    backgroundColor: ["transparent"],
  }).toString();
  // The icons style draws in white; everything after its clip mask is the
  // object, inked to match the agent illustrations.
  const maskEnd = object.indexOf("</mask>") + "</mask>".length;
  const inked =
    object.slice(0, maskEnd) +
    object.slice(maskEnd).replaceAll('fill="#fff"', `fill="${workThreadInk}"`);
  return svgDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><image href="${svgDataUri(ground)}" width="64" height="64"/><image href="${svgDataUri(inked)}" width="64" height="64"/></svg>`
  );
}

export function WorkThreadAvatar({
  seed,
  name,
}: {
  seed: string;
  name: string;
}) {
  const visual = useMemo(() => workThreadArt(seed), [seed]);
  return (
    <Avatar
      size="sm"
      visual={visual}
      name={name}
      className="shrink-0 rounded-xl ring-2 ring-app-background"
    />
  );
}
